import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatTRY } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Landmark, Trash2, Check, CalendarClock } from "lucide-react";

const empty = { bank: "", loan_type: "", total_amount: "", installment_amount: "", total_installments: 12, paid_installments: 0, payment_day: 15, start_date: new Date().toISOString().slice(0, 10) };
export default function Loans() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/loans").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.bank || !form.installment_amount || !form.total_installments) { toast.error("Banka, taksit tutarı ve taksit sayısı zorunlu"); return; }
    const payload = {
      bank: form.bank, loan_type: form.loan_type || "İşletme Kredisi",
      total_amount: parseFloat(form.total_amount) || 0,
      installment_amount: parseFloat(form.installment_amount) || 0,
      total_installments: parseInt(form.total_installments) || 1,
      paid_installments: parseInt(form.paid_installments) || 0,
      payment_day: parseInt(form.payment_day) || 15, start_date: form.start_date,
    };
    try {
      if (editId) await api.put(`/loans/${editId}`, payload);
      else await api.post("/loans", payload);
      toast.success(editId ? "Kredi güncellendi" : "Kredi eklendi, taksitler ödeme planına serildi");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };
  const del = async (id) => { await api.delete(`/loans/${id}`); toast.success("Silindi"); load(); };
  const pay = async (id) => { await api.patch(`/loans/${id}/pay`); toast.success("Taksit ödendi olarak işaretlendi"); load(); };
  const openEdit = (l) => { setEditId(l.id); setForm({ ...l }); setOpen(true); };

  const totalRemaining = items.reduce((s, l) => s + (l.total_installments - l.paid_installments) * l.installment_amount, 0);

  return (
    <div className="p-5 md:p-8 max-w-6xl">
      <PageHeader
        testid="loans-header"
        title="Kredi & Taksit Yönetimi"
        subtitle="Taksit tutarı ve toplam taksit sayısını girin; kalan tüm taksitler kredi bitene kadar ödeme takvimine otomatik eklenir."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <button data-testid="btn-add-loan" className="px-4 py-2.5 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] transition-colors font-semibold text-sm flex items-center gap-2 shrink-0">
                <Plus className="w-4 h-4" /> Kredi Ekle
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#161D2A] border-[#253043] text-[#F8FAFC]">
              <DialogHeader><DialogTitle className="font-display">{editId ? "Krediyi Düzenle" : "Yeni Kredi"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Banka"><input data-testid="loan-bank-input" value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} className={inp} placeholder="Ziraat Bankası" /></Field>
                <Field label="Kredi Türü">
                  <input
                    data-testid="loan-type-input"
                    value={form.loan_type || ""}
                    onChange={(e) => setForm({ ...form, loan_type: e.target.value })}
                    className={inp}
                    placeholder="Örn. Araç Kredisi, Ticari Kredi"
                  />
                </Field>
                <Field label="Toplam Kredi Tutarı (₺)"><input type="number" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} className={inp} placeholder="0" /></Field>
                <Field label="Taksit Tutarı (₺)"><input data-testid="loan-installment-amount-input" type="number" value={form.installment_amount} onChange={(e) => setForm({ ...form, installment_amount: e.target.value })} className={inp} placeholder="0" /></Field>
                <Field label="Toplam Taksit"><input data-testid="loan-total-installments-input" type="number" value={form.total_installments} onChange={(e) => setForm({ ...form, total_installments: e.target.value })} className={inp} /></Field>
                <Field label="Ödenen Taksit"><input type="number" value={form.paid_installments} onChange={(e) => setForm({ ...form, paid_installments: e.target.value })} className={inp} /></Field>
                <Field label="Ödeme Günü (ayın)"><input type="number" min="1" max="31" value={form.payment_day} onChange={(e) => setForm({ ...form, payment_day: e.target.value })} className={inp} /></Field>
                <Field label="İlk Taksit Tarihi"><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inp} /></Field>
              </div>
              <DialogFooter>
                <button data-testid="loan-save-button" onClick={save} className="px-4 py-2.5 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] transition-colors font-semibold text-sm w-full">{editId ? "Güncelle" : "Kaydet & Planla"}</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="card-surface p-5 mb-6 flex items-center gap-4 fade-up">
        <div className="w-11 h-11 rounded-xl bg-[#8B5CF6]/15 flex items-center justify-center">
          <Landmark className="w-5 h-5 text-[#8B5CF6]" />
        </div>
        <div>
          <div className="text-sm text-[#94A3B8]">Toplam Kalan Kredi Borcu</div>
          <div className="font-mono-num text-2xl font-bold" data-testid="loans-total-remaining">{formatTRY(totalRemaining)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {items.length === 0 && <div className="card-surface p-10 text-center text-[#94A3B8] text-sm lg:col-span-2">Henüz kredi eklenmedi.</div>}
        {items.map((l) => {
          const remaining = l.total_installments - l.paid_installments;
          const pct = (l.paid_installments / l.total_installments) * 100;
          const done = remaining <= 0;
          return (
            <div key={l.id} data-testid="loan-item-card" className="card-surface p-6 fade-up">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#8B5CF6]/15 flex items-center justify-center">
                    <Landmark className="w-5 h-5 text-[#8B5CF6]" />
                  </div>
                  <div>
                    <button onClick={() => openEdit(l)} className="font-display font-semibold text-left">{l.bank}</button>
                    <div className="text-xs text-[#94A3B8]">{l.loan_type}</div>
                  </div>
                </div>
                <button onClick={() => del(l.id)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <Stat label="Taksit" value={formatTRY(l.installment_amount)} />
                <Stat label="Kalan Taksit" value={`${remaining} / ${l.total_installments}`} />
                <Stat label="Ödeme Günü" value={`Ayın ${l.payment_day}.`} />
              </div>

              <div className="flex justify-between text-[11px] text-[#94A3B8] mb-1.5">
                <span>{l.paid_installments} taksit ödendi</span>
                <span className="font-mono-num">Kalan: {formatTRY(remaining * l.installment_amount)}</span>
              </div>
              <div className="h-2 rounded-full bg-[#111620] overflow-hidden mb-4">
                <div className="h-full rounded-full bg-[#8B5CF6] transition-all" style={{ width: `${pct}%` }} />
              </div>

              <button
                onClick={() => pay(l.id)} disabled={done} data-testid="loan-pay-installment"
                className={`w-full py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors ${done ? "bg-[#10B981]/15 text-[#10B981] cursor-default" : "bg-[#111620] hover:bg-[#1D2637] text-[#F8FAFC]"}`}
              >
                {done ? <><Check className="w-4 h-4" /> Kredi Tamamlandı</> : <><CalendarClock className="w-4 h-4" /> Bir Taksit Öde</>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const inp = "w-full px-3 py-2.5 rounded-xl bg-[#111620] border border-[#253043] focus:border-[#8B5CF6] outline-none transition-colors text-sm";
function Field({ label, children }) {
  return (<div><label className="block text-xs text-[#94A3B8] mb-1.5">{label}</label>{children}</div>);
}
function Stat({ label, value }) {
  return (
    <div className="p-3 rounded-xl bg-[#111620]">
      <div className="text-[11px] text-[#94A3B8] mb-1">{label}</div>
      <div className="font-mono-num font-bold text-sm">{value}</div>
    </div>
  );
}
